import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import { CREDENTIALS, PHASES } from './data/constants';
import MainPage from './pages/MainPage';
import AnalyticsPage from './pages/AnalyticsPage';
import LoginPage from './pages/LoginPage';
import './App.css';

const TOKEN_KEY = 'isolar_token';
const TOKEN_EXP_KEY = 'isolar_token_expires';
const TOKEN_TTL_MS = 24 * 60 * 60 * 1000; // 24h
//const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:4000';
const API_BASE = '/inokom/api';
const REAUTH_FLAG = 'isolar_must_reauth';

const getStoredToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const exp = Number(localStorage.getItem(TOKEN_EXP_KEY) || 0);
  if (!token || !exp || Date.now() > exp) return null;
  return token;
};

const isTokenValid = () => Boolean(getStoredToken());

const clearStoredToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXP_KEY);
};

const storeToken = (token) => {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + TOKEN_TTL_MS));
};

const getReauthFlag = () => sessionStorage.getItem(REAUTH_FLAG) === 'true';
const setReauthFlag = (val) => sessionStorage.setItem(REAUTH_FLAG, val ? 'true' : 'false');

function AppShell() {
  const [token, setToken] = useState(getStoredToken());
  const [mustReauth, setMustReauth] = useState(getReauthFlag());
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [error, setError] = useState('');

  const layoutImage = encodeURI(`${process.env.PUBLIC_URL}/Inokom_latest.jpg`);

  useEffect(() => {
    if (!token) {
      const cached = getStoredToken();
      if (cached) setToken(cached);
    }
  }, [token]);

  const handleLogin = async (event) => {
    event.preventDefault();
    if (
      credentials.username !== CREDENTIALS.username ||
      credentials.password !== CREDENTIALS.password
    ) {
      setError('Invalid username or password. Please try again.');
      return;
    }


    try {
      // If we already have a valid cached token, reuse it and skip supplier call
      const existing = getStoredToken();
      if (existing) {
        setToken(existing);
        setMustReauth(false);
        setReauthFlag(false);
        setError('');
        return;
      }

      const resp = await fetch(`${API_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!resp.ok) {
        throw new Error(`Login failed (${resp.status})`);
      }
      const data = await resp.json();
      console.log('Login response data:', data);
      if (!data.token) throw new Error('No token returned from supplier login');
      storeToken(data.token);
      setToken(data.token);
      setMustReauth(false);
      setReauthFlag(false);
      setError('');
    } catch (err) {
      console.error(err);
      setError('Supplier login failed. Please try again.');
    }
  };

  const handleLogout = () => {
    // Keep token cached; require re-auth in UI, persists across refresh via sessionStorage
    setMustReauth(true);
    setReauthFlag(true);
    setToken(getStoredToken());
    setCredentials({ username: '', password: '' });
    setError('');
  };

  const isAuthenticated = Boolean(token) && !mustReauth && isTokenValid();

  return (
    <Router basename="/inokom">
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <LoginPage
                credentials={credentials}
                error={error}
                onChange={(e) => {
                  const { name, value } = e.target;
                  setCredentials((prev) => ({ ...prev, [name]: value }));
                }}
                onSubmit={handleLogin}
              />
            )
          }
        />
        <Route
          path="/analytics/:phaseId"
          element={isAuthenticated ? <AnalyticsPage phases={PHASES} /> : <Navigate to="/login" replace />}
        />
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <MainPage phases={PHASES} layoutImage={layoutImage} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
      </Routes>
    </Router>
  );
}

export default function App() {
  return <AppShell/>;
}
